import React from "react";
import {
  FormControlLabel,
  Switch,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@mui/material";
import { withRouter } from "react-router-dom";
import { Box } from "@mui/material";
import {
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  Description as DocumentIcon,
  Movie as VideoIcon,
  AudioFile as AudioIcon,
  Archive as ArchiveIcon,
  Code as CodeIcon,
  TableChart as SpreadsheetIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
  formatDate,
  formatFileSize,
  getFileType as getTypeFromUtils,
} from "../utils/fileOperations";

const getFileIcon = (fileType) => {
  const iconProps = { sx: { fontSize: 48 } };
  switch (fileType) {
    case "folder":
      return (
        <FolderIcon {...iconProps} sx={{ ...iconProps.sx, color: "#FFB300" }} />
      );
    case "image":
      return (
        <ImageIcon {...iconProps} sx={{ ...iconProps.sx, color: "#E91E63" }} />
      );
    case "document":
      return (
        <DocumentIcon
          {...iconProps}
          sx={{ ...iconProps.sx, color: "#1565C0" }}
        />
      );
    case "spreadsheet":
      return (
        <SpreadsheetIcon
          {...iconProps}
          sx={{ ...iconProps.sx, color: "#4CAF50" }}
        />
      );
    case "video":
      return (
        <VideoIcon {...iconProps} sx={{ ...iconProps.sx, color: "#FF5722" }} />
      );
    case "audio":
      return (
        <AudioIcon {...iconProps} sx={{ ...iconProps.sx, color: "#9C27B0" }} />
      );
    case "archive":
      return (
        <ArchiveIcon
          {...iconProps}
          sx={{ ...iconProps.sx, color: "#795548" }}
        />
      );
    case "code":
      return (
        <CodeIcon {...iconProps} sx={{ ...iconProps.sx, color: "#607D8B" }} />
      );
    default:
      return (
        <FileIcon {...iconProps} sx={{ ...iconProps.sx, color: "#1565C0" }} />
      );
  }
};

const DetailSidebarContent = ({ file }) => {
  const { t } = useTranslation();
  const translation = t("fileExplorer");
  if (!file) return null;

  const getBreadcrumbPath = (file) => {
    const relativePath =
      file.path || file.webkitRelativePath || file.name || "";
    return `${translation.myFilesPath}${relativePath}`;
  };

  const fileType = getTypeFromUtils(file.name || "", file.type || "");

  return (
    <Box sx={{ p: 2, height: "100%", overflow: "auto" }}>
      <Box
        sx={{
          height: 100,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 3,
          backgroundColor: "#f8f9fa",
          borderRadius: 2,
          border: "1px solid #e9ecef",
        }}
      >
        {getFileIcon(fileType)}
      </Box>

      <FormControlLabel
        control={
          <Switch
            checked={file.offline || false}
            aria-label={translation.offline}
            size="small"
            onChange={() => {}}
          />
        }
        label={translation.availableOffline}
        sx={{ mb: 3, "& .MuiFormControlLabel-label": { fontSize: "0.875rem" } }}
      />

      <Typography
        variant="h6"
        sx={{ mb: 2, fontWeight: 600, fontSize: "1rem", color: "#333" }}
      >
        {translation.details}
      </Typography>

      <Table
        size="small"
        sx={{
          "& .MuiTableCell-root": {
            fontSize: "0.875rem",
            paddingY: "8px",
            paddingX: "12px",
            borderBottom: "1px solid #e0e0e0",
            verticalAlign: "middle",
          },
          "& .MuiTableCell-head": { fontWeight: 600 },
        }}
      >
        <TableBody>
          <TableRow>
            <TableCell
              component="th"
              sx={{
                fontWeight: 600,
                width: "40%",
                color: "#555",
                whiteSpace: "nowrap",
              }}
            >
              {translation.type}
            </TableCell>
            <TableCell sx={{ color: "#333", whiteSpace: "normal" }}>
              {file.type || fileType}
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell
              component="th"
              sx={{ fontWeight: 600, color: "#555", whiteSpace: "nowrap" }}
            >
              {translation.sortBySize}
            </TableCell>
            <TableCell sx={{ color: "#333" }}>
              {formatFileSize(file.size)}
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell
              component="th"
              sx={{ fontWeight: 600, color: "#555", whiteSpace: "nowrap" }}
            >
              {translation.location}
            </TableCell>
            <TableCell
              sx={{ color: "#333", wordBreak: "break-word" }}
              title={getBreadcrumbPath(file)}
            >
              {getBreadcrumbPath(file)}
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell
              component="th"
              sx={{ fontWeight: 600, color: "#555", whiteSpace: "nowrap" }}
            >
              {translation.modified}
            </TableCell>
            <TableCell sx={{ color: "#333" }}>
              {formatDate(file.updatedAt)}
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell
              component="th"
              sx={{ fontWeight: 600, color: "#555", whiteSpace: "nowrap" }}
            >
              {translation.created}
            </TableCell>
            <TableCell sx={{ color: "#333" }}>
              {formatDate(file.createdAt)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};

export default withRouter(DetailSidebarContent);
